import React, { useState, useEffect, useRef } from 'react';
import { db } from '../db';
import { ActivityCalendar } from 'react-activity-calendar';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend } from 'recharts';
import { X, Sparkles, TrendingUp, TrendingDown, Target, Edit2, Check, ShoppingBag, Gift } from 'lucide-react';
import { generateWeeklyAnalysis } from '../services/aiService';
import MuscleHeatmap from '../components/MuscleHeatmap';
import Loader from '../components/Loader';
import { shopItems } from '../data/shopItems';
import Mascot from '../components/Mascot';
import { getLevelInfo } from '../utils/xpUtils';
import { toast } from '../utils/feedback';
import { FeedbackModal } from '../components/FeedbackButton';
import SEO from '../components/SEO';
import { ChestIcon } from './Shop';
import { useNavigate } from 'react-router-dom';
import { subscribeToActiveBoss, getBossCycleInfo } from '../services/bossService';
import { isCardioExercise, getCardioType, formatSetDisplay, calculateWorkoutVolume } from '../utils/exerciseUtils';

const Dashboard = ({ currentProfile, theme }) => {
  const navigate = useNavigate();
  const [workouts, setWorkouts] = useState([]);
  const [exercisesMap, setExercisesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  
  // For Calendar Modal
  const [selectedDate, setSelectedDate] = useState(null);
  const activityCalendarScrollRef = useRef(null);

  const [weeklyAnalysis, setWeeklyAnalysis] = useState(null);
  const [isAnalyzingWeek, setIsAnalyzingWeek] = useState(true);
  const [weeklyVolumeData, setWeeklyVolumeData] = useState([]);
  
  // Mascot rename state
  const [isEditingMascotName, setIsEditingMascotName] = useState(false);
  const [mascotNameInput, setMascotNameInput] = useState('');
  const [boss, setBoss] = useState(null);

  useEffect(() => {
    const unsubBoss = subscribeToActiveBoss((b) => setBoss(b));
    return () => unsubBoss();
  }, []);

  const handleSaveMascotName = async () => {
    try {
      const updatedProfile = { ...currentProfile, mascotName: mascotNameInput.trim() };
      await db.updateProfile(updatedProfile);
      setIsEditingMascotName(false);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      const [data, exercisesData] = await Promise.all([
        db.getWorkouts(currentProfile.id),
        db.getExercises(currentProfile.id)
      ]);
      
      if (!isMounted) return;
      setWorkouts(data);
      setLoading(false);

      // Create a map of exercises to easily retrieve their muscle groups
      const exercisesMapLocal = {};
      if (exercisesData) {
        exercisesData.forEach(ex => {
          exercisesMapLocal[ex.id] = ex;
        });
        setExercisesMap(exercisesMapLocal);
      }

      // --- Weekly Analysis Logic ---
      if (data.length > 0) {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setHours(0, 0, 0, 0);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const recentWorkouts = data.filter(w => new Date(w.date) >= sevenDaysAgo);
        
        const fourteenDaysAgo = new Date(sevenDaysAgo);
        fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 7);
        
        const previousWorkouts = data.filter(w => {
            const date = new Date(w.date);
            return date >= fourteenDaysAgo && date < sevenDaysAgo;
        });

        if (recentWorkouts.length > 0 || previousWorkouts.length > 0) {
          const defaultMuscles = ['Quadriceps', 'Ischio-jambiers', 'Fessier', 'Dos', 'Pectoraux', 'Abdos', 'Épaules', 'Biceps', 'Triceps'];
          const dataByMuscle = {};
          defaultMuscles.forEach(m => {
            dataByMuscle[m] = { volume: 0, previousVolume: 0, sets: 0, previousSets: 0 };
          });
          
          const processWorkouts = (workoutsList, isPrevious) => {
            workoutsList.forEach(w => {
              w.exercises.forEach(block => {
                const items = block.type === 'superset' ? block.items : [block];
                items.forEach(ex => {
                  let def = exercisesMapLocal[ex.exerciseId];
                  if (!def) {
                    def = Object.values(exercisesMapLocal).find(e => e.name === ex.name) || {};
                  }
                  const guessMuscleFromName = (name) => {
                    if (!name) return 'Autre';
                    const lowerName = name.toLowerCase();
                    if (/(curl|ischio|soulevé|deadlift|sdt)/.test(lowerName)) return 'Ischio-jambiers';
                    if (/(squat|presse|leg|fente|quad|jambe)/.test(lowerName)) return 'Quadriceps';
                    if (/(mollet|calf)/.test(lowerName)) return 'Mollets';
                    if (/(hip thrust|fessier|glute)/.test(lowerName)) return 'Fessier';
                    if (/(traction|rowing|dos|pull|lat|tirage)/.test(lowerName)) return 'Dos';
                    if (/(pec|développé|bench|push|pompe|fly|chest|écarté)/.test(lowerName)) return 'Pectoraux';
                    if (/(épaule|shoulder|élévation|military|face pull)/.test(lowerName)) return 'Épaules';
                    if (/(bicep|curl)/.test(lowerName)) return 'Biceps';
                    if (/(tricep|extension|dips)/.test(lowerName)) return 'Triceps';
                    if (/(abdo|crunch|gainage|core|planche)/.test(lowerName)) return 'Abdos';
                    return 'Autre';
                  };

                  // On donne la priorité à la catégorie actuelle de la bibliothèque (def), plutôt qu'à l'ancienne catégorie enregistrée dans la séance (ex)
                  let rawMuscles = def.muscles || (def.muscle ? [def.muscle] : null) || ex.muscles || (ex.muscle ? [ex.muscle] : null);
                  if (!rawMuscles) {
                    rawMuscles = [guessMuscleFromName(ex.name)];
                  }
                  
                  const baseMusclesArray = Array.isArray(rawMuscles) ? rawMuscles : [rawMuscles];
                  const musclesArray = baseMusclesArray.map(m => {
                    if (m === 'Jambes') {
                      const lowerName = ex.name.toLowerCase();
                      if (/(curl|ischio|soulevé|souleve|deadlift|sdt|good morning|rdl|arrière cuisse|arriere cuisse)/.test(lowerName)) {
                        return 'Ischio-jambiers';
                      }
                      if (/(hip thrust|fessier|glute|abduct)/.test(lowerName)) {
                        return 'Fessier';
                      }
                      return 'Quadriceps';
                    }
                    return m;
                  });
                  
                  if (ex.sets) {
                    ex.sets.forEach(s => {
                      if (s.isWarmup) return;
                      const reps = Math.floor(Number(s.reps) || 0);
                      const repsL = Math.floor(Number(s.repsL) || 0);
                      const repsR = Math.floor(Number(s.repsR) || 0);
                      
                      const hasReps = reps > 0 || repsL > 0 || repsR > 0;
                      
                      if (hasReps) {
                        const weight = Number(s.weight) || 0;
                        const weightL = Number(s.weightL) || 0;
                        const weightR = Number(s.weightR) || 0;
                        
                        const setVolume = s.isUnilateral ? (repsL * weightL + repsR * weightR) : (reps * weight);
                        
                        musclesArray.forEach((muscleName, index) => {
                          const mName = muscleName || 'Autre';
                          if (!dataByMuscle[mName]) {
                            dataByMuscle[mName] = { volume: 0, previousVolume: 0, sets: 0, previousSets: 0 };
                          }
                          
                          const weightFactor = 1; // 100% pour tous les muscles ciblés (primaire et secondaires)
                          const adjustedVolume = setVolume * weightFactor;
                          
                          if (isPrevious) {
                            dataByMuscle[mName].previousVolume += adjustedVolume;
                            dataByMuscle[mName].previousSets += weightFactor;
                          } else {
                            dataByMuscle[mName].volume += adjustedVolume;
                            dataByMuscle[mName].sets += weightFactor;
                          }
                        });
                      }
                    });
                  }
                });
              });
            });
          };

          processWorkouts(recentWorkouts, false);
          processWorkouts(previousWorkouts, true);

          const volumeDataArray = Object.keys(dataByMuscle)
            .filter(muscle => muscle !== 'Autre' && muscle !== 'Avant-bras' && (defaultMuscles.includes(muscle) || dataByMuscle[muscle].volume > 0 || dataByMuscle[muscle].previousVolume > 0))
            .map(muscle => ({
              muscle,
              volume: dataByMuscle[muscle].volume,
              previousVolume: dataByMuscle[muscle].previousVolume,
              sets: dataByMuscle[muscle].sets,
              previousSets: dataByMuscle[muscle].previousSets
            }))
            .sort((a, b) => {
              const indexA = defaultMuscles.indexOf(a.muscle);
              const indexB = defaultMuscles.indexOf(b.muscle);
              if (indexA === -1 && indexB === -1) return 0;
              if (indexA === -1) return 1;
              if (indexB === -1) return -1;
              return indexA - indexB;
            });
          
          setWeeklyVolumeData(volumeDataArray);

          const latestWorkoutDate = new Date(Math.max(...recentWorkouts.map(w => new Date(w.date).getTime()))).getTime();
          const dbAnalysis = await db.getLatestWeeklyAnalysis(currentProfile.id);

          if (dbAnalysis && dbAnalysis.latestWorkoutDate === latestWorkoutDate && dbAnalysis.version === 15) {
            setWeeklyAnalysis(dbAnalysis.analysis);
            setIsAnalyzingWeek(false);
          } else {
            setIsAnalyzingWeek(true);
            const generatedAnalysis = await generateWeeklyAnalysis(volumeDataArray, currentProfile);
            if (generatedAnalysis && isMounted) {
              setWeeklyAnalysis(generatedAnalysis);
              await db.saveWeeklyAnalysis(currentProfile.id, {
                analysis: generatedAnalysis,
                latestWorkoutDate: latestWorkoutDate,
                version: 15
              });
            }
            if (isMounted) setIsAnalyzingWeek(false);
          }
        } else {
           setIsAnalyzingWeek(false);
        }
      } else {
        setIsAnalyzingWeek(false);
      }
    };
    if (currentProfile) loadData();
    return () => { isMounted = false; };
  }, [currentProfile?.id]);

  useEffect(() => {
    if (!loading && activityCalendarScrollRef.current) {
      const scrollToRight = () => {
        if (activityCalendarScrollRef.current) {
          activityCalendarScrollRef.current.scrollLeft = activityCalendarScrollRef.current.scrollWidth;
        }
      };
      scrollToRight();
      const timer = setTimeout(scrollToRight, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, workouts]);

  if (loading || !currentProfile) return <Loader message="Analyse des performances..." />;

  const totalWorkouts = workouts.length;

  // Function to safely get local YYYY-MM-DD string
  const getLocalDateString = (date) => {
    return new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
  };

  // Prepare data by date
  const workoutsPerDate = {};
  const volumePerDate = {};

  workouts.forEach(w => {
    const d = new Date(w.date);
    const dateStr = getLocalDateString(d);
    
    workoutsPerDate[dateStr] = workoutsPerDate[dateStr] || [];
    workoutsPerDate[dateStr].push(w);
    
    // Calculate volume
    const vol = w.totalVolume !== undefined ? w.totalVolume : calculateWorkoutVolume(w);
    volumePerDate[dateStr] = (volumePerDate[dateStr] || 0) + vol;
  });

  // Generate calendar data (exactly 150 days back from today, roughly 5 months)
  const calendarData = [];
  const today = new Date();
  
  for (let i = 150; i >= 0; i--) {
    const d = new Date();
    d.setDate(today.getDate() - i);
    const dateStr = getLocalDateString(d);
    
    const count = workoutsPerDate[dateStr] ? workoutsPerDate[dateStr].length : 0;
    
    // Level calculation for colors (binary: workout or no workout)
    let level = count > 0 ? 1 : 0;

    calendarData.push({
      date: dateStr,
      count: count,
      level: level
    });
  }

  // Chart data for recent 20 active days (Volume)
  const chartData = Object.keys(volumePerDate)
    .sort((a, b) => new Date(a) - new Date(b))
    .map(date => ({
      date,
      volume: volumePerDate[date]
    })).slice(-15); // Last 15 active days

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const sessionsLast30Days = workouts.filter(w => new Date(w.date) >= thirtyDaysAgo).length;

  let tonnage30Days = 0;
  let totalTime30Days = 0;
  workouts.forEach(w => {
    if (new Date(w.date) >= thirtyDaysAgo) {
      if (w.duration) {
        totalTime30Days += Number(w.duration);
      }
      tonnage30Days += (w.totalVolume !== undefined ? w.totalVolume : calculateWorkoutVolume(w));
    }
  });
    
  // Format Date for XAxis
  const formatXAxisDate = (tickItem) => {
    const d = new Date(tickItem);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const formatTooltipDate = (label) => {
    const d = new Date(label);
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).replace(/^\w/, c => c.toUpperCase());
  };

  const renderWorkoutModal = () => {
    if (!selectedDate) return null;
    
    const dateStr = getLocalDateString(selectedDate);
    const dayWorkouts = workoutsPerDate[dateStr] || [];

    return (
      <div 
        onClick={() => setSelectedDate(null)}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      >
        <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: '90%', maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto' }}>
          <div className="flex justify-between items-center mb-4">
            <h2>Aperçu</h2>
            {currentProfile?.name === 'Junakin' && !hasRefundedXP && (
              <button 
                className="btn btn-primary" 
                onClick={handleRefundXP}
                style={{ backgroundColor: 'var(--secondary-color)', color: 'white', border: 'none', boxShadow: '0 0 10px rgba(46, 204, 113, 0.5)' }}
              >
                🎁 Récupérer mes XP perdus (+350 XP)
              </button>
            )}
            <button className="btn-icon" onClick={() => setSelectedDate(null)}><X size={20} /></button>
          </div>
          
          <div className="mb-4 border-b border-border-color pb-2">
            <h3>Activité du {selectedDate.toLocaleDateString('fr-FR')}</h3>
          </div>
          
          {dayWorkouts.length === 0 ? (
            <p className="text-secondary text-center my-4">Aucune séance ce jour-là.</p>
          ) : (
            <div className="flex-col gap-3">
              {dayWorkouts.map(w => (
                <div key={w.id} style={{ backgroundColor: 'var(--bg-color)', padding: '1rem', borderRadius: 'var(--border-radius-sm)' }}>
                  <h4 style={{ color: 'var(--primary-color)' }}>{w.name}</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                    {new Date(w.date).toLocaleTimeString('fr-FR', {hour: '2-digit', minute:'2-digit'})}
                    {w.feeling && ` • Ressenti: ${w.feeling === 'easy' ? '😊' : w.feeling === 'normal' ? '😐' : w.feeling === 'hard' ? '🥵' : '💀'}`}
                    {w.restTime && ` • Repos: ${w.restTime} min`}
                    {w.duration && ` • ⏱ ${w.duration >= 60 ? `${Math.floor(w.duration / 60)}h${w.duration % 60 > 0 ? (w.duration % 60).toString().padStart(2, '0') : ''}` : `${w.duration} min`}`}
                  </p>
                  
                  {w.exercises.map((block, idx) => {
                    const isSuperset = block.type === 'superset';
                    const items = isSuperset ? block.items : [block];
                    return (
                      <div key={idx} style={{ marginBottom: '0.5rem', borderLeft: isSuperset ? '3px solid var(--secondary-color)' : 'none', paddingLeft: isSuperset ? '0.5rem' : '0' }}>
                        {isSuperset && <div style={{fontWeight:'bold', color:'var(--secondary-color)', marginBottom:'0.2rem'}}>🔗 Superset</div>}
                        {items.map((ex, iIdx) => {
                          const isCardio = isCardioExercise(ex, Object.values(exercisesMap));
                          const cardioType = isCardio ? getCardioType(ex.name) : 'generic';
                          return (
                            <div key={ex.exerciseId || iIdx} style={{ marginBottom: '0.5rem' }}>
                              <strong>{ex.name}{ex.equipment && ex.equipment !== 'Autre' ? <span style={{ color: 'var(--text-secondary)', fontWeight: 'normal', fontSize: '0.9em' }}> ({ex.equipment})</span> : ''}</strong>
                              <div style={{ paddingLeft: '1rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                                {ex.sets && ex.sets.map((set, i) => (
                                  <div key={i} style={{ 
                                    marginLeft: set.isDropSet ? '1rem' : '0', 
                                    color: set.isDropSet ? 'var(--secondary-color)' : (set.isWarmup ? '#f39c12' : 'inherit'),
                                    fontSize: set.isDropSet ? '0.8rem' : 'inherit',
                                    marginTop: '0.2rem'
                                  }}>
                                    {set.isDropSet ? '↳ Dégressive : ' : (set.isWarmup ? '🔥 Échauffement : ' : `Série : `)}
                                    {formatSetDisplay(set, isCardio, cardioType)}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const equippedTitle = shopItems.find(i => i.id === currentProfile?.equipped?.title);
  const equippedMascot = currentProfile?.equipped?.mascot;
  const level = getLevelInfo(currentProfile?.xp || 0).level;

  const todayDateString = new Date().toISOString().split('T')[0];
  const isChestAvailable = currentProfile?.lastDailyChestDate !== todayDateString;

  const bossCycle = boss?.cycleInfo || getBossCycleInfo();
  const isBossDefeated = boss && (boss.status === 'defeated' || (boss.currentHp || 0) <= 0);
  const isBossExpired = boss && (boss.status === 'expired' || (!isBossDefeated && bossCycle.isRestPeriod));
  const isRaidFinished = isBossDefeated || isBossExpired;

  const shouldShowBossBanner = () => {
    if (!boss) return false;
    return bossCycle.showBanner;
  };

  return (
    <div>
      <SEO title="Tableau de bord" />
      {/* Title removed per user request */}

      {/* BANNIÈRE WORLD BOSS RAID */}
      {shouldShowBossBanner() && (
        <div 
          className="world-boss-banner"
          onClick={() => navigate('/social?tab=boss')}
          style={{
            marginBottom: '1.2rem',
            padding: '0.85rem 1.1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.8rem',
            background: isRaidFinished 
              ? 'linear-gradient(135deg, rgba(107, 114, 128, 0.16) 0%, rgba(140, 127, 191, 0.14) 100%)' 
              : 'linear-gradient(135deg, rgba(140, 127, 191, 0.18) 0%, rgba(110, 181, 131, 0.14) 100%)',
            border: isRaidFinished 
              ? '1.5px solid rgba(140, 127, 191, 0.4)' 
              : '1.5px solid var(--primary-color, #8c7fbf)',
            borderRadius: '16px',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(140, 127, 191, 0.25)',
            transition: 'transform 0.15s ease'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
            <div className="boss-banner-icon" style={{ fontSize: '1.8rem', lineHeight: 1 }}>
              {isBossDefeated ? '🏆' : (isBossExpired ? '💀' : '🐉')}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: '0.95rem', color: 'var(--text-primary, #f0eef5)', whiteSpace: 'nowrap' }}>
                  World Boss<span className="hide-on-mobile"> : {boss?.name || 'Dracofonte'}</span>
                </strong>
                <span style={{ 
                  fontSize: '0.68rem', 
                  fontWeight: 800, 
                  backgroundColor: isRaidFinished ? 'rgba(107, 114, 128, 0.35)' : 'var(--primary-color, #8c7fbf)', 
                  color: isRaidFinished ? '#d1d5db' : '#fff', 
                  padding: '0.12rem 0.45rem', 
                  borderRadius: '5px',
                  whiteSpace: 'nowrap'
                }}>
                  {isBossDefeated ? 'RAID TERMINÉ 🏆' : (isBossExpired ? 'RAID TERMINÉ 💀' : 'RAID ACTIF ⚔️')}
                </span>
              </div>
              <div className="boss-banner-subtitle" style={{ fontSize: '0.78rem', color: 'var(--text-secondary, #9a94ab)', marginTop: '0.15rem', lineHeight: '1.25' }}>
                {isBossDefeated ? (
                  'Boss vaincu ! Consulte les résultats'
                ) : isBossExpired ? (
                  'Boss non vaincu ! Une prochaine fois'
                ) : (
                  <>
                    <span>Événement communautaire</span>
                    <span className="boss-subtitle-separator"> • </span>
                    <span className="boss-subtitle-line2">Entre dans l'arène</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <button 
            className="btn btn-primary boss-banner-btn"
            style={{
              padding: '0.62rem 1.25rem',
              fontSize: '0.92rem',
              fontWeight: 800,
              borderRadius: '10px',
              border: 'none',
              background: isRaidFinished 
                ? 'linear-gradient(135deg, #4b5563 0%, #374151 100%)' 
                : 'var(--primary-color)',
              color: '#fff',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            {isRaidFinished ? 'Bilan du Raid' : 'Accéder'}
          </button>
        </div>
      )}

      {isChestAvailable && (
        <div 
          className="card daily-chest-banner" 
          style={{ 
            marginBottom: '1.5rem', 
            padding: '1rem', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            gap: '1rem',
            background: 'linear-gradient(135deg, rgba(110, 181, 131, 0.14) 0%, rgba(46, 204, 113, 0.08) 100%)', 
            border: '1.5px solid rgba(110, 181, 131, 0.4)',
            borderRadius: '16px',
            boxShadow: '0 4px 16px rgba(110, 181, 131, 0.25)',
            cursor: 'pointer',
            transition: 'transform 0.15s ease'
          }}
          onClick={() => navigate('/shop', { state: { activeTab: 'chest' } })}
        >
          <div className="daily-chest-left" style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', minWidth: 0, flex: 1 }}>
            <div className="daily-chest-icon-wrapper" style={{ 
              width: '54px', 
              height: '54px', 
              borderRadius: '50%', 
              backgroundColor: 'rgba(46, 204, 113, 0.1)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              boxShadow: '0 0 10px rgba(46, 204, 113, 0.3)',
              flexShrink: 0
            }}>
              <div style={{ transform: 'scale(0.75)', marginLeft: '-10px', marginRight: '-10px', marginTop: '-5px' }}>
                <ChestIcon type="chest_basic" />
              </div>
            </div>
            <div className="daily-chest-info" style={{ minWidth: 0 }}>
              <h3 style={{ margin: 0, color: 'var(--secondary-color)', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Ton coffre t'attend ! <Sparkles size={16} />
              </h3>
              <p style={{ margin: '0.2rem 0 0 0', color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: '1.3' }}>
                Récupère tes gains quotidiens
              </p>
            </div>
          </div>
          <button 
            className="btn btn-primary daily-chest-btn" 
            style={{ 
              backgroundColor: 'var(--secondary-color)', 
              color: '#000', 
              fontWeight: 'bold', 
              whiteSpace: 'nowrap',
              border: 'none',
              padding: '0.62rem 1.35rem',
              fontSize: '0.95rem',
              borderRadius: '10px',
              flexShrink: 0
            }}
          >
            Ouvrir
          </button>
        </div>
      )}

      {totalWorkouts === 0 && (
        <div className="card text-center" style={{ padding: '4rem 2rem', margin: '2rem 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <img src="/logos/man_lifting_v6.svg" alt="Bienvenue" style={{ width: '100px', height: '100px' }} />
          </div>
          <h3 style={{ color: 'var(--primary-color)', marginBottom: '1rem' }}>Bienvenue dans Masse-terclass !</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', maxWidth: '500px' }}>
            Tu n'as pas encore enregistré de séance d'entraînement. Commence dès maintenant pour suivre ta progression, analyser tes performances et débloquer des succès !
          </p>
        </div>
      )}
      <>
          {/* WEEKLY ANALYSIS SECTION */}
          {(weeklyVolumeData.length > 0) && (
        <div className="card mb-3" style={{ border: '1px solid rgba(162, 155, 254, 0.3)' }}>
          <h3 className="weekly-analysis-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--primary-color)', marginBottom: '1.5rem' }}>
            <Sparkles size={20} /> Bilan des 7 derniers jours
          </h3>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem' }}>
            {/* Radar Chart */}
            <div className="weekly-radar-container">
              {weeklyVolumeData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart outerRadius="78%" data={weeklyVolumeData.map(d => ({ ...d, muscle: d.muscle.replace(/ischio-?jambiers/i, 'Ischios') }))}>
                    <PolarGrid stroke="rgba(168, 159, 207, 0.55)" strokeWidth={1.3} />
                    <PolarAngleAxis dataKey="muscle" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 'auto']} stroke="rgba(168, 159, 207, 0.4)" tick={false} axisLine={false} />
                    <Radar
                      name="Volume (7 jours précédents)"
                      dataKey="previousVolume"
                      stroke="var(--secondary-color)"
                      strokeWidth={1.5}
                      fill="var(--secondary-color)"
                      fillOpacity={0.25}
                    />
                    <Radar
                      name="Volume (7 derniers jours)"
                      dataKey="volume"
                      stroke="var(--primary-color)"
                      strokeWidth={2}
                      fill="var(--primary-color)"
                      fillOpacity={0.7}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: 'var(--bg-color)', border: '1px solid var(--primary-color)', borderRadius: 'var(--border-radius-sm)', color: 'var(--text-primary)' }}
                      formatter={(value, name) => [`${Math.round(value * 10) / 10} kg`, name === 'Volume (7 derniers jours)' ? '7 derniers jours' : '7 jours précédents']}
                    />
                    <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: '0.85rem', paddingTop: '10px' }} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic', textAlign: 'center' }}>
                  Aucune donnée d'entraînement à afficher pour cette période.
                </div>
              )}
            </div>

            {/* AI Insights */}
            <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: '1rem', justifyContent: 'center' }}>
              {isAnalyzingWeek ? (
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>
                  <div className="spinner" style={{ margin: '0 auto 1rem auto' }}></div>
                  Génération de l'analyse IA en cours...
                </div>
              ) : weeklyAnalysis ? (
                <>
                  <div style={{ backgroundColor: 'var(--insight-success-bg)', padding: '1rem', borderRadius: 'var(--border-radius)', border: '1px solid var(--insight-success-border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--insight-success-text)', fontWeight: 'bold', marginBottom: '0.3rem' }}>
                      <TrendingUp size={18} /> Point Fort
                    </div>
                    <p style={{ margin: 0, fontSize: '0.9rem' }}>{weeklyAnalysis.forts}</p>
                  </div>
                  
                  <div style={{ backgroundColor: 'var(--insight-danger-bg)', padding: '1rem', borderRadius: 'var(--border-radius)', border: '1px solid var(--insight-danger-border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--insight-danger-text)', fontWeight: 'bold', marginBottom: '0.3rem' }}>
                      <TrendingDown size={18} /> Point Faible
                    </div>
                    <p style={{ margin: 0, fontSize: '0.9rem' }}>{weeklyAnalysis.faibles}</p>
                  </div>

                  <div style={{ backgroundColor: 'var(--insight-warning-bg)', padding: '1rem', borderRadius: 'var(--border-radius)', border: '1px solid var(--insight-warning-border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--insight-warning-text)', fontWeight: 'bold', marginBottom: '0.3rem' }}>
                      <Target size={18} /> Conseil de la semaine
                    </div>
                    <p style={{ margin: 0, fontSize: '0.9rem' }}>{weeklyAnalysis.conseil}</p>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                  Analyse IA indisponible.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Section Principale (Heatmap + Statistiques) */}
      <div className="dashboard-top-row" style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
        gap: '2rem', 
        marginBottom: '2rem',
        alignItems: 'stretch'
      }}>
        
        {/* HEATMAP MUSCULAIRE (Colonne Gauche) */}
        <div className="card heatmap-column" style={{ padding: '0', height: '100%', display: 'flex', flexDirection: 'column' }}>
          <MuscleHeatmap 
            key={currentProfile?.id || 'heatmap'}
            exercisesMap={exercisesMap}
            recentWorkouts={
              workouts.filter(w => {
                const workoutDate = new Date(w.date);
                const fourteenDaysAgo = new Date();
                fourteenDaysAgo.setHours(0, 0, 0, 0); // Include full day
                fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
                return workoutDate >= fourteenDaysAgo;
              })
            } 
          />
        </div>

        {/* STATISTIQUES (Colonne Droite, Grille 2x2) */}
        <div className="stats-column" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: '1.5rem', height: '100%' }}>
          <div className="card text-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <h3 style={{ textAlign: 'center' }}>Total Séances</h3>
            <p style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--primary-color)', textAlign: 'center' }}>{totalWorkouts}</p>
          </div>
          <div className="card text-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <h3 style={{ textAlign: 'center' }}>Séances (30 derniers jours)</h3>
            <p style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--secondary-color)', textAlign: 'center' }}>{sessionsLast30Days}</p>
          </div>
          <div className="card text-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <h3 style={{ textAlign: 'center' }}>Tonnage (30 derniers jours)</h3>
            <p style={{ fontSize: '2.5rem', fontWeight: 'bold', color: theme ? 'var(--secondary-color)' : '#a29bfe', textAlign: 'center' }}>
              {tonnage30Days >= 1000 ? `${(tonnage30Days / 1000).toFixed(1)}t` : `${tonnage30Days}kg`}
            </p>
          </div>
          <div className="card text-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <h3 style={{ textAlign: 'center' }}>Temps d'entraînement (30 jours)</h3>
            <p style={{ fontSize: '2.5rem', fontWeight: 'bold', color: theme ? 'var(--primary-color)' : '#fab1a0', textAlign: 'center' }}>
              {totalTime30Days >= 60 ? `${Math.floor(totalTime30Days / 60)}h${totalTime30Days % 60 > 0 ? (totalTime30Days % 60).toString().padStart(2, '0') : ''}` : `${totalTime30Days} min`}
            </p>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="card">
          <h3 style={{ marginBottom: '1rem' }}>Calendrier Mensuel</h3>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>Clique sur un jour en couleur pour voir le détail.</p>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Calendar 
              className="custom-calendar"
              onClickDay={(value) => setSelectedDate(value)}
              tileClassName={({ date, view }) => {
                if (view === 'month') {
                  const dateString = getLocalDateString(date);
                  const todayString = getLocalDateString(new Date());
                  const hasWorkout = !!workoutsPerDate[dateString];
                  const isToday = dateString === todayString;

                  if (isToday) {
                    return hasWorkout ? 'today-has-workout' : 'today-no-workout';
                  }
                  if (hasWorkout) {
                    return 'highlight-workout';
                  }
                }
                return null;
              }}
            />
          </div>
        </div>

        <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ marginBottom: '1rem' }}>Activité des 6 derniers mois</h3>
          <div ref={activityCalendarScrollRef} className="activity-calendar-wrapper" style={{ flex: 1, display: 'flex', alignItems: 'center', overflowX: 'auto', paddingBottom: '1rem' }}>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', margin: 'auto' }}>
              <ActivityCalendar 
                data={calendarData} 
              maxLevel={1}
              hideColorLegend={true}
              blockSize={16}
              blockMargin={5}
              weekStart={1}
              theme={{
                light: ['rgba(128, 128, 128, 0.15)', 'var(--secondary-color)'],
                dark: ['rgba(128, 128, 128, 0.2)', 'var(--secondary-color)'],
              }}
              colorScheme={theme || "light"}
              labels={{
                weekdays: ['D', 'L', 'M', 'M', 'J', 'V', 'S'],
                legend: {
                  less: 'Repos',
                  more: 'Entraînement'
                },
                totalCount: '{{count}} séances ces 6 derniers mois',
              }}
            />
            {/* Custom right labels */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '5px', 
              marginTop: '23px', // Offset for month labels
              marginLeft: '8px', 
              fontSize: '11px', 
              color: 'var(--text-secondary)' 
            }}>
              <span style={{ height: '16px', display: 'flex', alignItems: 'center' }}>L</span>
              <span style={{ height: '16px', display: 'flex', alignItems: 'center' }}>M</span>
              <span style={{ height: '16px', display: 'flex', alignItems: 'center' }}>M</span>
              <span style={{ height: '16px', display: 'flex', alignItems: 'center' }}>J</span>
              <span style={{ height: '16px', display: 'flex', alignItems: 'center' }}>V</span>
              <span style={{ height: '16px', display: 'flex', alignItems: 'center' }}>S</span>
              <span style={{ height: '16px', display: 'flex', alignItems: 'center' }}>D</span>
            </div>
            </div>
          </div>
        </div>
      </div>

      {totalWorkouts > 0 && chartData.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginBottom: '1.5rem' }}>
          <div className="card" style={{ flex: '2 1 400px', margin: 0 }}>
            <h3>Évolution : Volume Total (kg)</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
              Volume = (Poids × Répétitions) cumulé sur toutes tes séries. Reflète ton effort total par séance.
            </p>
            <div style={{ height: '350px', marginTop: '1rem', overflow: 'hidden' }} className="hide-scrollbar">
              <div style={{ minWidth: '100%', height: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ bottom: 50, top: 10, right: 10, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                    <XAxis dataKey="date" stroke="var(--text-secondary)" tick={{fontSize: 11}} tickFormatter={formatXAxisDate} angle={-45} textAnchor="end" dx={-5} dy={5} />
                    <YAxis stroke="var(--text-secondary)" tick={{fontSize: 12}} />
                    <Tooltip 
                      cursor={{ fill: 'rgba(168, 159, 207, 0.1)' }}
                      contentStyle={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)', borderRadius: '8px' }}
                      labelFormatter={formatTooltipDate}
                      formatter={(value) => [`${value} kg`, 'Volume Total']}
                    />
                    <Bar dataKey="volume" fill="var(--primary-color)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          
          {equippedMascot && (
            <div className="card" style={{ flex: '1 1 250px', margin: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem' }}>
                {isEditingMascotName ? (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', background: 'var(--bg-color)', padding: '0.3rem 0.5rem', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                    <input 
                      type="text" 
                      value={mascotNameInput} 
                      onChange={(e) => setMascotNameInput(e.target.value)}
                      style={{ padding: '0.2rem', borderRadius: '4px', border: 'none', backgroundColor: 'transparent', color: 'var(--text-primary)', maxWidth: '120px', outline: 'none', fontWeight: 'bold', textAlign: 'center' }}
                      autoFocus
                      placeholder="Nom..."
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveMascotName()}
                    />
                    <button className="btn-icon" onClick={handleSaveMascotName} style={{ color: 'var(--primary-color)', padding: '0.1rem' }}><Check size={16} /></button>
                    <button className="btn-icon" onClick={() => setIsEditingMascotName(false)} style={{ color: 'var(--danger-color)', padding: '0.1rem' }}><X size={16} /></button>
                  </div>
                ) : (
                  <h3 style={{ margin: 0, textAlign: 'center', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {currentProfile.mascotName || 'Ton Compagnon'}
                    <button className="btn-icon" onClick={() => { setIsEditingMascotName(true); setMascotNameInput(currentProfile.mascotName || ''); }} style={{ color: 'var(--text-secondary)', padding: '0.2rem' }}>
                      <Edit2 size={16} />
                    </button>
                  </h3>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, width: '100%' }}>
                <Mascot id={equippedMascot} level={level} size={320} previewAccessory={currentProfile?.equipped?.mascot_accessory} />
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', fontStyle: 'italic', textAlign: 'center', padding: '0 1rem' }}>
                <span style={{ fontWeight: 'bold', color: 'var(--primary-color)' }}>{currentProfile.mascotName || 'Ton Compagnon'}</span> t'offre 10% d'XP et pièces supplémentaires ! Pas mal, non ?
              </div>
            </div>
          )}
        </div>
      )}
      </>

      {/* Bouton de signalement pour mobile au bas du Dashboard */}
      <div style={{ padding: '1rem', display: 'flex', justifyContent: 'center', marginTop: '1rem' }} className="mobile-only-feedback">
        <style>{`
          @media (min-width: 768px) {
            .mobile-only-feedback {
              display: none !important;
            }
          }
        `}</style>
        <button 
          onClick={() => setShowFeedbackModal(true)}
          style={{
            height: '40px',
            padding: '0 20px',
            borderRadius: '20px',
            backgroundColor: 'var(--card-bg)',
            color: 'var(--text-secondary)',
            border: '1px solid var(--primary-color)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          <div style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            border: '2px solid var(--primary-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.8rem',
            fontWeight: 'bold',
            color: 'var(--primary-color)'
          }}>?</div>
          <span style={{ color: 'var(--primary-color)' }}>Signaler un bug / problème ?</span>
        </button>
      </div>

      {showFeedbackModal && <FeedbackModal currentProfile={currentProfile} onClose={() => setShowFeedbackModal(false)} />}
      
      {renderWorkoutModal()}
    </div>
  );
};



export default Dashboard;

